import type { ComponentType, ReactNode } from 'react';
import { BuildingOfficeIcon, UserPlusIcon } from '@interstate/components/Icons';
import type { CoBuyer, IShopper } from '@makemydeal/dr-platform-types';
import { searchBusinessByName } from '../../store/businessConversionActionCreators';
import { searchConsumersByName } from '../../store/consumerActionCreators';
import { toDraftFromConsumerResult } from '../../mappers/consumerSearchMapper';
import { BUSINESS, CARD_OPERATION_KIND } from '../../constants';
import type { CardOperationKind } from '../../types/cardOperation.types';
import type { BusinessEntity } from '../../types/businessConversion.types';
import type { ConsumerSearchResult, SearchResult, SearchRowProps } from '../../types/search.types';
import BusinessResultRow from './BusinessResultRow';
import ConsumerResultRow from './ConsumerResultRow';

export interface BuildDraftArgs {
    /** The chosen result, or `null` when the dealer is creating a new record. */
    selected: SearchResult | null;
    /** What they typed into the search box. */
    typedText: string;
    /** The participant this operation targets — business conversion keeps their details. */
    participant?: IShopper | CoBuyer | null;
    isPostalCodeDigitsOnlyValidationEnabled?: boolean;
}

/**
 * How one operation searches, keyed by its kind. Only operations with a search step have an entry:
 * a swap interchanges the two people already on the deal, so it has neither search nor a draft.
 */
export interface SearchDescriptor {
    testIdPrefix: string;
    heading: string;
    searchLabel: string;
    placeholder: string;
    addNewLabel: string;
    addNewIcon: ReactNode;
    /** The thunk that runs the search for this operation's entity type. */
    run: (query: string) => unknown;
    RowComponent: ComponentType<SearchRowProps>;
    /** How the search step exits: a chosen result — or the absence of one — becomes the edit step's draft. */
    buildDraft: (args: BuildDraftArgs) => IShopper | CoBuyer | null;
}

const businessDescriptor: SearchDescriptor = {
    testIdPrefix: 'business-search',
    heading: 'Convert to Business',
    searchLabel: 'Search for Business',
    placeholder: 'Enter business name...',
    addNewLabel: 'Add New Business',
    addNewIcon: <BuildingOfficeIcon />,
    run: (query) => searchBusinessByName(query),
    // Each row takes its own entity type, narrower than the result union. The descriptor and the
    // results it renders always come from the same operation, but that invariant isn't expressible.
    RowComponent: BusinessResultRow as SearchDescriptor['RowComponent'],
    // Conversion keeps the same person and makes them a business, so the draft spreads them.
    buildDraft: ({ selected, typedText, participant }) => {
        if (!participant) return null;
        const business = selected as BusinessEntity | null;
        return {
            ...participant,
            customerType: BUSINESS,
            business: {
                ...(participant as IShopper).business,
                legalName: business?.businessName ?? typedText,
                businessEntityId: business?.businessEntityId ?? ''
            }
        } as IShopper;
    }
};

/** Consumer search is identical for every operation that picks a person; only the heading differs. */
const CONSUMER_SEARCH: Omit<SearchDescriptor, 'heading'> = {
    testIdPrefix: 'consumer-search',
    searchLabel: 'Search for Customer',
    placeholder: 'Enter customer name...',
    addNewLabel: 'Create New Customer',
    addNewIcon: <UserPlusIcon />,
    run: (query) => searchConsumersByName(query),
    RowComponent: ConsumerResultRow as SearchDescriptor['RowComponent'],
    // A change replaces the person, so the chosen result becomes the draft outright. With no result
    // the dealer is creating someone, and an empty-but-present draft is what asks for a blank form.
    buildDraft: ({ selected, isPostalCodeDigitsOnlyValidationEnabled }) =>
        selected
            ? toDraftFromConsumerResult(selected as ConsumerSearchResult, isPostalCodeDigitsOnlyValidationEnabled)
            : ({} as IShopper)
};

const consumerSearch = (heading: string): SearchDescriptor => ({ ...CONSUMER_SEARCH, heading });

const DESCRIPTORS: Partial<Record<CardOperationKind, SearchDescriptor>> = {
    [CARD_OPERATION_KIND.CONVERT_TO_BUSINESS]: businessDescriptor,
    [CARD_OPERATION_KIND.CHANGE_BUYER]: consumerSearch('Change Buyer'),
    [CARD_OPERATION_KIND.CHANGE_CO_BUYER]: consumerSearch('Change Co-Buyer'),
    [CARD_OPERATION_KIND.ADD_CUSTOMER]: consumerSearch('Add Customer'),
    [CARD_OPERATION_KIND.ADD_CO_BUYER]: consumerSearch('Add Co-Buyer')
};

export const getSearchDescriptor = (kind?: CardOperationKind | null): SearchDescriptor | undefined =>
    kind ? DESCRIPTORS[kind] : undefined;
